import useBackdrop from '@common/components/backdrop/useBackdrop';
import { useAppSelector } from '@common/hooks/store';
import { selectUserCountryCode } from '@features/auth/slice/authSlice';
import { MoneyGateBank } from '@features/deposit/screens/money-gate/api/moneyGate.type';
import {
  useLazyCheckMoneyGateOrderStatusQuery,
  useUpdateMoneyGateOrderBankMutation,
} from '@features/deposit/screens/money-gate/api/moneyGateApi';
import DepositOnlineMoneyGateBanks from '@features/deposit/screens/money-gate/zone/tj/DepositOnlineMoneyGateBanks';
import DepositOnlineMoneyGateRequisites
  from '@features/deposit/screens/money-gate/zone/tj/DepositOnlineMoneyGateRequisites';
import { requestSurveyTrigger } from '@features/survey/runtime/surveyBus';
import { useSnackbar } from 'notistack';
import React, { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface IMoneyGateContainerProps {
  moneyGateBanks?: MoneyGateBank[];
  orderId: string;
  defaultBank?: string | null;
  defaultType?: string | null;
  activeStep: number;
  setActiveStep: (value: number) => void;
}

interface IHandleChangeActiveStep {
  step: number;
}

interface IHandleSelectBank {
  bank?: string;
  orderId: string;
  type?: string;
}

const DepositOnlineMoneyGateContainer: FC<IMoneyGateContainerProps> = ({ moneyGateBanks, setActiveStep, activeStep, defaultBank, defaultType, orderId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const backdrop = useBackdrop();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();
  const countryCode = useAppSelector(selectUserCountryCode)?.toLowerCase();
  const [ updateMoneyGateOrderBank, { data, isSuccess } ] = useUpdateMoneyGateOrderBankMutation();
  const [ checkStatusGate, { data: requisiteStatus } ] = useLazyCheckMoneyGateOrderStatusQuery();
  const didInit = useRef(false);

  const handleChangeActiveStep = useCallback(({ step }: IHandleChangeActiveStep) => {
    setActiveStep(step);
  }, [ setActiveStep ]);

  const handleSelectBank = useCallback(({ type, bank, orderId }: IHandleSelectBank) => {
    updateMoneyGateOrderBank({ type, bank, orderId }).unwrap().then((res) => {
      intervalRef.current = setInterval(() => {
        checkStatusGate({orderId: res.orderId}).then();
      }, 5000);
    }).catch((e) => {
      navigate('/deposit');
      enqueueSnackbar(e.data, { variant: 'error' });
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    });
  }, [ enqueueSnackbar, updateMoneyGateOrderBank ]);


  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const initialize = async () => {
      backdrop(true);
      try {
        if (countryCode === 'ru') {
          setActiveStep(1);
          await handleSelectBank({ orderId });
        } else if (orderId && defaultBank && defaultType) {
          setActiveStep(0);
          await handleSelectBank({
            orderId,
            bank: defaultBank,
            type: defaultType,
          });
        }
      } finally {
        backdrop(false);
      }
    };

    initialize();
  }, [backdrop, countryCode, defaultBank, defaultType, handleSelectBank, orderId]);


  useEffect(() => {
    if (requisiteStatus && (
      requisiteStatus.status === 'customer_confirm' ||
      requisiteStatus.status === 'completed' ||
      requisiteStatus.status === 'cancelled' ||
      requisiteStatus.status === 'dispute' ||
      requisiteStatus.status === 'rejected' ||
      requisiteStatus.status === 'error')
    ) {
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [ requisiteStatus ]);

  useEffect(() => {
    if (requisiteStatus && requisiteStatus.status === 'completed') {
      setIsLoading(false);
      enqueueSnackbar('Оплата прошла успешно', { variant: 'success' });
      requestSurveyTrigger('deposit_success');
      navigate('/account-history');
    }
  }, [enqueueSnackbar, navigate, requisiteStatus]);

  useEffect(() => {
    if (requisiteStatus && (
      requisiteStatus.status === 'cancelled' ||
      requisiteStatus.status === 'dispute' ||
      requisiteStatus.status === 'rejected' ||
      requisiteStatus.status === 'error')
    ) {
      setIsLoading(false);
      enqueueSnackbar('Произошла ошибка', { variant: 'error' });
      navigate('/deposit');
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [enqueueSnackbar, navigate, requisiteStatus])



  return (
    <Fragment>

      {activeStep === 0 && moneyGateBanks && (
        <DepositOnlineMoneyGateBanks banks={moneyGateBanks}
                        disabled={!!(defaultBank && defaultType)}
                        onSelectBank={handleSelectBank} orderId={orderId}
                        handleChangeActiveStep={handleChangeActiveStep}/>
      )}

      {activeStep === 1 && data && (
        <DepositOnlineMoneyGateRequisites gateData={data} isLoading={isLoading} />
      )}
    </Fragment>
  );
};

export default DepositOnlineMoneyGateContainer;